import { useState, useEffect, useMemo } from "react";
import { Avatar } from "components/avatar";
import { NoStyleA, OutlineButton } from "components/button";
import { useModalContext } from "components/modal";
import TableComponent from "components/table";
import { format, fromUnixTime, parseISO, isValid } from "date-fns";
import { RiArrowRightLine } from "react-icons/ri";
import { TPayment, TDepositRequest as TDepositRequestFull } from "types/payment";
import { dollarFormatter } from "utils";
import Placeholder from "views/request/component/placeholder";
import ApplyDepositModal from "./applyDepositModal";
import { useTranslation } from 'react-i18next';
import { enUS, es } from "date-fns/locale";
import UnapplyPaymentModal from "./unapplyPaymentModal";
import TooltippedElement from "components/TooltippedElement";
import { TbNotesOff } from "react-icons/tb";
import { Button } from "components/button";
import axios from "axios";
import Spinner from "components/spinner";
import { useAuthContext } from "contexts/auth";

type Props = {
  data: (TPayment[] | null) | ((TPayment | null)[] | undefined);
  type: "request" | "invoice" | "project" | "all" | "customer";
  table?: any;
  fixedColumnWidths?: boolean;
};

type TDepositRequest = Pick<
TDepositRequestFull,
  "id" | "amount_requested" | "amount_received" | "status"
>;

export default function PaymentsList(props: Props) {
  const { t, i18n } = useTranslation();
  const tBase = "containers.payments.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { setModal, modal } = useModalContext();
  const locale = i18n.language === "es" ? es : enUS;
  const auth = useAuthContext();

  const [depositRequests, setDepositRequests] = useState<TDepositRequest[]>([]);
  const [unapplyingPaymentId, setUnapplyingPaymentId] = useState<number | null>(null);

  useEffect(() => {
    if (!Array.isArray(props.data)) return;
  
    const ids = Array.from(
      new Set(
        props.data
          .map((p) => p?.fk_deposit_request_id)
          .filter((id): id is number => typeof id === "number")
      )
    );
  
    if (!ids.length) return;
  
    axios
      .get<TDepositRequest[]>(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/deposit-request`, {
        params: { ids },
      })
      .then((res) => setDepositRequests(res.data))
      .catch(console.error);
  }, [props.data]);

  const drMap = useMemo(
    () => new Map(depositRequests.map((dr) => [dr.id, dr])),
    [depositRequests]
  );

  // and in the render, splice the deposit_request in
  // const augmentedData = useMemo(() => {
  //   return (props.data as TPayment[] | undefined)?.map((p) =>
  //     p?.fk_deposit_request_id
  //       ? { ...p, deposit_request: drMap.get(p.fk_deposit_request_id) }
  //       : p
  //   );
  // }, [props.data, drMap]);

  const augmentedData = useMemo(() => {
    if (!Array.isArray(props.data)) return [];
  
    return props.data.map((p) =>
      p?.fk_deposit_request_id
        ? { ...p, deposit_request: drMap.get(p.fk_deposit_request_id) }
        : p
    );
  }, [props.data, drMap]);

  const columns = {
    id: {
      label: "ID",
      width: "4rem",
      minWidth: "4rem",
    },
    amount: {
      label: tr("Amount"),
      width: "7rem",
      minWidth: "6rem",
      cell: (row: TPayment) => (
        row.fk_deposit_request_id && row.deposit_request && row.deposit_request.amount_requested !== row.deposit_request.amount_received ? (
          <TooltippedElement 
            placement="bottom"
            message={`${tr("Received")} ${dollarFormatter(row.deposit_request.amount_received || 0)} / ${tr("Requested")} ${row.deposit_request.amount_requested && dollarFormatter(row.deposit_request.amount_requested)}`}
            element={
              <div className="font-bold">
                <div>
                  {dollarFormatter(row.deposit_request.amount_received || 0)} /
                </div>
                <div>
                  {row.deposit_request.amount_requested && dollarFormatter(row.deposit_request.amount_requested)}
                </div>
              </div>
            }
          />
        ) : (
          <span className="font-bold">
            {dollarFormatter(
              props.type === "invoice" ? row.used_amount : row.amount
            )}
          </span>
        )
      ),
    },
    is_deposit: {
      label: tr("Type"),
      width: "6rem",
      minWidth: "5rem",
      cell: (row: TPayment) =>
        row.is_voided ? (
          <span className="bg-red-100 px-2 text-[0.8rem] uppercase rounded text-red-700 font-bold ">
            {tr("Voided")}
          </span>
        ) : row.is_deposit ? (
          <span className="bg-blue-100 px-2 text-[0.8rem] uppercase rounded text-blue-700 font-bold ">
            {tr("Deposit")}
          </span>
        ) : row.is_write_off ? (
          <span className="bg-purple-100 px-2 text-[0.8rem] uppercase rounded text-purple-700 font-bold ">
            {tr("Write-Off")}
          </span>
        ) : (
          <span className="px-2 text-[0.8rem] uppercase rounded font-bold bg-green-100 text-green-700">
            {tr("Payment")}
          </span>
        ),
    },
    customer: {
      label: tr("Customer"),
      width: "14rem",
      minWidth: "10rem",
      maxWidth: "220px",
      style: { maxWidth: "220px" },
      cell: (row: TPayment) =>
        row.billed_to && (
          <div className="min-w-0 overflow-hidden truncate">
            <Avatar size="xsmall" showLabel>
              {row.billed_to.name}
            </Avatar>
          </div>
        ),
    },
    stripe_id: {
      label: tr("Method"),
      width: "14rem",
      minWidth: "10rem",
      maxWidth: "220px",
      style: { maxWidth: "220px" },
      cell: (row: TPayment) => {
        if (row.stripe_id && !row.fk_deposit_request_id) {
          return (
            <span className="flex text-blue-600">
              Stripe {row.payment_method === "ACH" ? "(ACH)" : tr("(card)")}
            </span>
          );
        } else if (row.fk_deposit_request_id && row.deposit_request) {
          const { status, decline_note } = row.deposit_request;
          return (
            <div className="flex items-center">
              <span>{tr("Deposit Request")}</span>
              <span className="ml-1">{"("}</span>
              <span className={`mx-0.5 px-2 text-[0.8rem] rounded font-bold 
                ${status === "Paid" 
                  ? "bg-[#e0ede5] text-[#288741]" 
                  : status === "Partially Paid" 
                  ? "bg-[#fce7dc] text-[#e55b06]" 
                  : status === "Declined" || status === "Voided"
                  ? "bg-[#f8d7da] text-[#dc3545]"
                  : "bg-[#fff2de] text-[#fea420]"}
              `}>
                {tr(status)}
              </span>
              <span>{")"}</span>
              <span>
                {decline_note && (
                  <TooltippedElement
                    message={tr("View Decline Note")}
                    placement="top"
                    element={
                      <button
                        onClick={(e) => {
                          // dont open main link, just open modal
                          e.preventDefault();
                          setModal({
                            label: tr("Decline Note"),
                            component: (
                              <div className="p-4 text-gray-700 max-w-[500px]">
                                <div className="whitespace-pre-wrap mb-4">
                                  {decline_note}
                                </div>
                                <Button
                                  type="secondary"
                                  onClick={() => setModal()}
                                >
                                  {tr("Close")}
                                </Button>
                              </div>
                            )
                          })
                        }}
                        className="p-2 rounded-full bg-slate-100 hover:bg-slate-300 transition duration-200"
                      >
                        <TbNotesOff size={20} className="text-yellow-600" />
                      </button>
                    }
                  />
                )}
              </span>
            </div>
          );
        } else {
          return `${tr("Manual")} (${row.payment_method && tr(row.payment_method)})`;
        }
      },
    },
    credit_ref: {
      label: tr("Credit To"),
      width: "6rem",
      minWidth: "5rem",
      isClickable: true,
      cell: (row: TPayment) =>
        row.fk_request_id ? (
          <NoStyleA
            to={`/requests/${row.fk_request_id}`}
            className="!text-blue-600 hover:!underline"
          >
            {tr("Req")}#{row.fk_request_id}
          </NoStyleA>
        ) : row.fk_project_id ? (
          <NoStyleA
            to={`/projects/${row.fk_project_id}`}
            className="!text-blue-600 hover:!underline"
          >
            {tr("Proj")}#{row.fk_project_id}
          </NoStyleA>
        ) : (
          <NoStyleA
            to={`/customers/${row.fk_bcustomer_id}`}
            className="!text-blue-600 hover:!underline"
          >
            {tr("Cust")}#{row.fk_bcustomer_id}
          </NoStyleA>
        ),
    },
    time: {
      label: tr("Entry"),
      width: "8rem",
      minWidth: "7rem",
      cell: (row: TPayment) => {
        try {
          if (row.received_on === null) {
            if (row.created === null) {
              return <span className="text-red-500">{tr("Unknown")}</span>;
            } else {
              const date = fromUnixTime(row.created);

              return isValid(date)
                ? format(date, "MMM dd, yyyy", {
                  locale: locale,
                })
                : tr("Invalid Date");
            }
          } else {
            // Assuming received_on is in 'YYYY-MM-DD' format
            const receivedDate = parseISO(row.received_on);

            return isValid(receivedDate)
              ? format(receivedDate, "MMM dd, yyyy", {
                locale: locale,
              })
              : tr("Invalid Date");
          }
        } catch (error) {
          console.error(
            `Error formatting date: ${row.received_on || row.created}`,
            error
          );
          return tr("Invalid Date");
        }
      },
    },
    invoice: {
      label: tr("Balance"),
      width: "9rem",
      minWidth: "7rem",
      isClickable: true,
      cell: (row: TPayment) => {
        // Helper function to check if this row should show deposit-related actions
        const canApplyDeposit = () => {
          const isValidType = props.type === "request" || props.type === "project" || props.type === "customer";
          const hasNoParentIds = !row.fk_request_id && !row.fk_project_id;
          return isValidType || hasNoParentIds;
        };

        // Helper function to check if we should show the balance text
        const shouldShowBalanceText = () => {
          const isValidType = props.type === "request" || props.type === "project" || props.type === "customer";
          const hasNoParentIds = !row.fk_request_id && !row.fk_project_id;
          const isAllType = props.type === "all";
          return !row.is_voided && row.remaining_total < 0 && (isValidType || hasNoParentIds || isAllType);
        };

        // Helper function to check if we should show the apply button
        const shouldShowApplyButton = () => {
          return (
            row.remaining_total > 0 &&
            props.type !== "invoice" &&
            canApplyDeposit()
          );
        };

        // Handle balance text display (negative balance)
        if (shouldShowBalanceText()) {
          return (
            <span>
              {tr("Balance")} {row.remaining_total && dollarFormatter(row.remaining_total)}
            </span>
          );
        }

        // Handle apply deposit button (positive balance)
        if (shouldShowApplyButton()) {
          return (
            <OutlineButton
              primary
              size="xsmall"
              onClick={() =>
                setModal({
                  component: (
                    <ApplyDepositModal
                      // @ts-ignore
                      type={props.type === "all" ? "customer" : props.type}
                      data={row}
                    />
                  ),
                  label: tr("Apply Deposit"),
                })
              }
            >
              {tr("Apply")} {dollarFormatter(row.remaining_total)}{" "}
              <RiArrowRightLine className="!text-xs" />
            </OutlineButton>
          );
        }

        // Default: show formatted remaining total
        return row.is_voided ? dollarFormatter(0) : dollarFormatter(row.remaining_total);
      },
    },
  };


  // adds "Unapply" column for the payment list in an invoice (/invoices/:id)
  const allowedUserIds = [255, 121, 145];
  const currentUserId = auth.user_id

  if (
    props.type === "invoice"
  ) {
    (columns as any).unapply = {
      label: tr("Unapply"),
      width: "6rem",
      minWidth: "5rem",
      isClickable: true,
      cell: (row: TPayment) => {
        const isUnapplying = unapplyingPaymentId === row.id;

        if (!row.is_voided && row.used_amount > 0) {
          return (
            <div className="flex items-center gap-2">
              <OutlineButton
                size="xsmall"
                className="!text-red-600 hover:!underline disabled:!text-gray-400 disabled:!cursor-not-allowed"
                disabled={isUnapplying}
                onClick={() => {
                  setModal({
                    label: tr("Unapply Payment"),
                    component: (
                      <UnapplyPaymentModal
                        data={row}
                        setUnapplyingPaymentId={setUnapplyingPaymentId}
                      />
                    ),
                  });
                }}
              >
                {isUnapplying ? (
                  <div className="flex items-center gap-1">
                    <Spinner size={12} color="#dc2626" />
                    {tr("Unapplying...")}
                  </div>
                ) : (
                  tr("Unapply")
                )}
              </OutlineButton>
            </div>
          );
        } else {
          return <span className="text-gray-500">{tr("N/A")}</span>;
        }
      },
    };
  }

  return (
    <div>
      {props.data ? (
        <TableComponent
          // data={props.data}
          data={augmentedData}
          columns={columns}
          to={(row: any) => `/payments/${row.id}`}
          {...(props.table || {})}
          fixedColumnWidths={props.fixedColumnWidths}
        />
      ) : (
        <Placeholder
          type="payment"
          header={tr("No payment yet.")}
          label={tr("Payments will appear here when added.")}
        />
      )}
    </div>
  );
}
